import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Calendar, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { errorMsg } from "@/lib/utils-app";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function DayPicker({ value, onChange, disabled }) {
  const toggle = (d) => {
    if (disabled) return;
    const set = new Set(value || []);
    if (set.has(d)) set.delete(d); else set.add(d);
    onChange([...set].sort((a, b) => a - b));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {DAY_LABELS.map((lbl, idx) => {
        const sel = (value || []).includes(idx);
        return (
          <button
            key={idx}
            type="button"
            onClick={() => toggle(idx)}
            disabled={disabled}
            data-testid={`sched-day-${lbl.toLowerCase()}`}
            className={`rounded-full border px-3 py-1 text-xs transition ${sel ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "hover:bg-secondary"}`}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

export default function SupervisorSchedulesPage() {
  const [rows, setRows] = useState([]);
  const [sups, setSups] = useState([]);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ supervisor_id: "", park_id: "", days_of_week: [], active: true, notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u, p] = await Promise.all([
        api.get("/admin/supervisor-schedules"),
        api.get("/users", { params: { role: "pmc_supervisor" } }),
        api.get("/parks"),
      ]);
      setRows(r.data || []);
      setSups(u.data || []);
      setParks(p.data || []);
    } catch (e) {
      toast.error(errorMsg(e, "Failed to load schedules"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const parkMap = useMemo(() => Object.fromEntries(parks.map((p) => [p.id, p])), [parks]);
  const supMap = useMemo(() => Object.fromEntries(sups.map((u) => [u.id, u])), [sups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.supervisor_name || "").toLowerCase().includes(q) ||
      (r.park_name || "").toLowerCase().includes(q) ||
      (r.park_code || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openNew = () => {
    setEditing(null);
    setForm({ supervisor_id: "", park_id: "", days_of_week: [0, 1, 2, 3, 4], active: true, notes: "" });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      supervisor_id: r.supervisor_id,
      park_id: r.park_id,
      days_of_week: r.days_of_week || [],
      active: r.active !== false,
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const availableParksFor = (sid) => {
    const sup = supMap[sid];
    if (!sup) return parks;
    return parks.filter((p) => (sup.park_ids || []).includes(p.id));
  };

  const save = async () => {
    if (!form.supervisor_id) return toast.error("Pick a supervisor");
    if (!form.park_id) return toast.error("Pick a park");
    if (!(form.days_of_week || []).length) return toast.error("Select at least one day");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/supervisor-schedules/${editing.id}`, {
          days_of_week: form.days_of_week,
          active: form.active,
          notes: form.notes || null,
        });
        toast.success("Schedule updated");
      } else {
        await api.post("/admin/supervisor-schedules", {
          supervisor_id: form.supervisor_id,
          park_id: form.park_id,
          days_of_week: form.days_of_week,
          active: form.active,
          notes: form.notes || null,
        });
        toast.success("Schedule saved");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete schedule for ${r.supervisor_name} at ${r.park_name}?`)) return;
    try {
      await api.delete(`/admin/supervisor-schedules/${r.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AppShell title="Supervisor Schedules" subtitle="Admin-fixed weekly park visit schedule">
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4 text-primary" />
              Weekly visit schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Search supervisor or park..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="sched-search"
              />
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} data-testid="sched-new-button">
                  <Plus className="mr-2 h-4 w-4" /> Add schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editing ? "Edit schedule" : "New schedule"}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Supervisor</label>
                    <select
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-background"
                      value={form.supervisor_id}
                      onChange={(e) => setForm({ ...form, supervisor_id: e.target.value, park_id: "" })}
                      disabled={!!editing}
                      data-testid="sched-supervisor-select"
                    >
                      <option value="">— Select supervisor —</option>
                      {sups.map((u) => (
                        <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Park</label>
                    <select
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-background"
                      value={form.park_id}
                      onChange={(e) => setForm({ ...form, park_id: e.target.value })}
                      disabled={!!editing}
                      data-testid="sched-park-select"
                    >
                      <option value="">— Select park —</option>
                      {availableParksFor(form.supervisor_id).map((p) => (
                        <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                      ))}
                    </select>
                    {form.supervisor_id && availableParksFor(form.supervisor_id).length === 0 && (
                      <div className="mt-1 text-[11px] text-amber-700">This supervisor has no assigned parks yet.</div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Days of week</label>
                    <div className="mt-2">
                      <DayPicker value={form.days_of_week} onChange={(v) => setForm({ ...form, days_of_week: v })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
                    <Input
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="e.g. Morning rounds before lunch"
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div className="text-sm">Schedule active</div>
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={save} disabled={saving} data-testid="sched-save">{saving ? "Saving…" : "Save"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 p-10 text-center">
                <Users className="h-8 w-8 text-slate-400" />
                <div className="text-sm text-slate-500">No schedules yet. Click "Add schedule" above to start.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left">Supervisor</th>
                      <th className="px-4 py-2 text-left">Park</th>
                      <th className="px-4 py-2 text-left">Days</th>
                      <th className="px-4 py-2 text-left">Active</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} className="border-t" data-testid={`sched-row-${r.id}`}>
                        <td className="px-4 py-2 font-medium text-slate-700">{r.supervisor_name}<div className="text-[11px] text-slate-500">{r.supervisor_username}</div></td>
                        <td className="px-4 py-2">{r.park_code} — {r.park_name}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {DAY_LABELS.map((lbl, idx) => (
                              <span key={idx} className={`rounded-full px-2 py-0.5 text-[10px] ${(r.days_of_week || []).includes(idx) ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-400"}`}>
                                {lbl}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <Badge className={r.active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-200 text-slate-700 hover:bg-slate-200"}>
                            {r.active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`sched-edit-${r.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`sched-delete-${r.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
